import i18n, { DEFAULT_LANGUAGE } from '../../i18n';
import { normalizeTimeInputValue } from './timeInput';

export const DEFAULT_UI_LANGUAGE = DEFAULT_LANGUAGE;

export const resolveUiTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

/** User's current local IANA zone. Prefer resolveUiTimeZone() when the zone can change. */
export const DEFAULT_UI_TIME_ZONE = resolveUiTimeZone();

const localeByLanguage: Record<string, string> = {
  az: 'az-Latn-AZ',
  en: 'en-US',
  tr: 'tr-TR',
};

const longMonthNamesByLanguage: Record<string, string[]> = {
  az: ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avqust', 'sentyabr', 'oktyabr', 'noyabr', 'dekabr'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
  tr: ['ocak', 'şubat', 'mart', 'nisan', 'mayıs', 'haziran', 'temmuz', 'ağustos', 'eylül', 'ekim', 'kasım', 'aralık'],
};

const yesterdayLabelByLanguage: Record<string, string> = {
  az: 'Dünən',
  en: 'Yesterday',
  tr: 'Dün',
};

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATE_TIME_INPUT_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/;

const DATE_TIME_COMPONENT_OPTION_KEYS: Array<keyof Intl.DateTimeFormatOptions> = [
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'dayPeriod',
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'timeZoneName',
];

const hasExplicitDateTimeComponents = (options?: Intl.DateTimeFormatOptions): boolean => {
  if (!options) {
    return false;
  }

  return DATE_TIME_COMPONENT_OPTION_KEYS.some(key => options[key] !== undefined);
};

const pad2 = (value: number): string => String(value).padStart(2, '0');

export const resolveUiLanguage = (language?: string | null): string => {
  const normalized = (language || '').trim().toLowerCase();
  return normalized || DEFAULT_UI_LANGUAGE;
};

export const resolveUiLocale = (language?: string | null): string => {
  const normalized = resolveUiLanguage(language);
  return localeByLanguage[normalized] || localeByLanguage[DEFAULT_UI_LANGUAGE];
};

export const toUiDate = (value: string | number | Date): Date | null => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }

  const dateOnly = DATE_ONLY_PATTERN.exec(trimmed);
  if (dateOnly) {
    const date = new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
};

const withUiTimeZone = (options?: Intl.DateTimeFormatOptions): Intl.DateTimeFormatOptions => ({
  timeZone: resolveUiTimeZone(),
  ...options,
});

export const formatDateTime = (value: string | number | Date, options?: Intl.DateTimeFormatOptions): string => {
  const date = toUiDate(value);
  if (!date) return '';

  if (!hasExplicitDateTimeComponents(options)) {
    return formatLocalizedDateTimeLabel(date);
  }

  return new Intl.DateTimeFormat(resolveUiLocale(i18n.resolvedLanguage), withUiTimeZone(options)).format(date);
};

export const formatDate = (value: string | number | Date, options?: Intl.DateTimeFormatOptions): string => {
  const date = toUiDate(value);
  if (!date) return '';

  if (!hasExplicitDateTimeComponents(options)) {
    return formatLongDateLabel(date);
  }

  return new Intl.DateTimeFormat(resolveUiLocale(i18n.resolvedLanguage), withUiTimeZone(options)).format(date);
};

export const formatTime = (value: string | number | Date, options?: Intl.DateTimeFormatOptions): string => {
  const date = toUiDate(value);
  if (!date) return '';

  const baseOptions = hasExplicitDateTimeComponents(options)
    ? {}
    : {
        timeStyle: 'short' as const,
      };

  return new Intl.DateTimeFormat(resolveUiLocale(i18n.resolvedLanguage), withUiTimeZone({
    ...baseOptions,
    ...options,
  })).format(date);
};

const getDatePartsInUiTimeZone = (date: Date): { year: number; month: number; day: number } | null => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }

  return { year, month, day };
};

export const formatLongDateLabel = (value: string | number | Date): string => {
  const date = toUiDate(value);
  if (!date) return '';

  const language = resolveUiLanguage(i18n.resolvedLanguage);
  const dateParts = getDatePartsInUiTimeZone(date);
  if (!dateParts) return '';

  const monthNames = longMonthNamesByLanguage[language] || longMonthNamesByLanguage[DEFAULT_UI_LANGUAGE];
  const monthName = monthNames[dateParts.month - 1];
  if (!monthName) return '';

  return `${dateParts.year} ${monthName} ${dateParts.day}`;
};

const formatDayMonthLabel = (
  dateParts: { year: number; month: number; day: number },
  includeYear = false,
): string => {
  const language = resolveUiLanguage(i18n.resolvedLanguage);
  const monthNames = longMonthNamesByLanguage[language] || longMonthNamesByLanguage[DEFAULT_UI_LANGUAGE];
  const monthName = monthNames[dateParts.month - 1];
  if (!monthName) return '';

  if (includeYear) {
    return `${dateParts.year} ${monthName} ${dateParts.day}`;
  }

  return `${dateParts.day} ${monthName}`;
};

const getYesterdayPartsInUiTimeZone = (now: Date): { year: number; month: number; day: number } | null => {
  const nowParts = getDatePartsInUiTimeZone(now);
  if (!nowParts) return null;

  const yesterday = new Date(nowParts.year, nowParts.month - 1, nowParts.day - 1);
  return getDatePartsInUiTimeZone(yesterday);
};

export const formatConversationListTimestamp = (value: string | number | Date, now: Date = new Date()): string => {
  const date = toUiDate(value);
  const nowDate = toUiDate(now);
  if (!date || !nowDate) return date ? formatDateTime(date) : '';

  const dateParts = getDatePartsInUiTimeZone(date);
  const nowParts = getDatePartsInUiTimeZone(nowDate);
  if (!dateParts || !nowParts) return formatDateTime(date);

  const isToday = dateParts.year === nowParts.year
    && dateParts.month === nowParts.month
    && dateParts.day === nowParts.day;
  if (isToday) {
    return formatTime(date);
  }

  const yesterdayParts = getYesterdayPartsInUiTimeZone(nowDate);
  const isYesterday = yesterdayParts
    && dateParts.year === yesterdayParts.year
    && dateParts.month === yesterdayParts.month
    && dateParts.day === yesterdayParts.day;
  if (isYesterday) {
    const language = resolveUiLanguage(i18n.resolvedLanguage);
    return yesterdayLabelByLanguage[language] || yesterdayLabelByLanguage[DEFAULT_UI_LANGUAGE];
  }

  return formatDayMonthLabel(dateParts, dateParts.year !== nowParts.year);
};

export const formatDateTimeInputValue = (value: string | number | Date): string => {
  const date = toUiDate(value);
  if (!date) return '';

  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

export const parseDateTimeInputValue = (value: string): Date | null => {
  const rendered = value.trim();
  if (!rendered) return null;

  const match = DATE_TIME_INPUT_PATTERN.exec(rendered);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  const seconds = Number(match[6] || 0);

  const parsed = new Date(year, month - 1, day, hours, minutes, seconds, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatDateInputValue = (value: string | number | Date): string => {
  const formatted = formatDateTimeInputValue(value);
  return formatted ? formatted.slice(0, 10) : '';
};

export const toDateInputValue = (value?: string | null): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return '';
  }

  if (DATE_ONLY_PATTERN.test(trimmed)) {
    return trimmed;
  }

  return formatDateInputValue(trimmed);
};

export const parseDateInputValue = (value: string): Date | null => {
  const rendered = value.trim();
  if (!rendered) {
    return null;
  }

  const match = DATE_ONLY_PATTERN.exec(rendered);
  if (!match) {
    return null;
  }

  const parsed = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 0, 0, 0, 0);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const splitDateTimeInputValue = (value: string): { date: string; time: string } => {
  const trimmed = value.trim();
  if (!trimmed.includes('T')) {
    return { date: trimmed.slice(0, 10), time: '' };
  }

  const [date = '', time = ''] = trimmed.split('T');
  return { date, time: time.slice(0, 5) };
};

export const formatLocalizedDateTimeLabel = (value: string | number | Date): string => {
  const date = toUiDate(value);
  if (!date) return '';

  const dateLabel = formatLongDateLabel(date);
  const { time } = splitDateTimeInputValue(formatDateTimeInputValue(date));
  if (!dateLabel) {
    return time;
  }

  if (!time) {
    return dateLabel;
  }

  return `${dateLabel}, ${time}`;
};

export const combineDateTimeInputValue = (date: string, time: string): string => {
  const normalizedDate = date.trim();
  if (!normalizedDate) {
    return '';
  }

  const normalizedTime = time.trim() || '00:00';
  return `${normalizedDate}T${normalizedTime}`;
};

export const buildTimeSelectOptions = (minuteStep = 5, selectedTime?: string): string[] => {
  const step = Math.max(1, Math.min(60, minuteStep));
  const options = new Set<string>();

  for (let hour = 0; hour < 24; hour += 1) {
    for (let minute = 0; minute < 60; minute += step) {
      options.add(`${pad2(hour)}:${pad2(minute)}`);
    }
  }

  const selected = selectedTime?.trim().slice(0, 5);
  if (selected && /^\d{2}:\d{2}$/.test(selected)) {
    options.add(selected);
  }

  return Array.from(options).sort();
};

export const formatTimeInputLabel = (value?: string | null): string => {
  const normalized = normalizeTimeInputValue(value || '');
  if (!normalized) {
    return '';
  }

  const [hour, minute] = normalized.split(':').map(Number);
  const date = new Date(2000, 0, 1, hour, minute, 0, 0);

  return new Intl.DateTimeFormat(resolveUiLocale(i18n.resolvedLanguage), {
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const formatTaskDueDateLabel = (value?: string | null): string => {
  const normalized = toDateInputValue(value);
  if (!normalized) {
    return '';
  }

  return formatLongDateLabel(normalized);
};

export const applyDocumentLocalization = (language?: string | null) => {
  if (typeof document === 'undefined') return;

  const resolvedLanguage = resolveUiLanguage(language || i18n.resolvedLanguage);
  document.documentElement.lang = resolvedLanguage;
  document.documentElement.dir = 'ltr';
};
